// ============================================================================
// ROLE API TYPES
// ============================================================================
// Types for the Role Master module — matches tenant-hub API DTOs:
//   - RoleResponse.cs
//   - CreateRoleRequest.cs / UpdateRoleRequest.cs
//   - KeycloakRoleResponse.cs
//   - RoleExistenceValidation (IRoleService.cs)
//   - RoleScopeAssignmentResponse.cs / RoleScopeAssignmentRequest.cs

import type { ID } from './common.types';

// ============================================================================
// ROLE
// ============================================================================

/**
 * Role — matches RoleResponse.cs.
 * Note: the API exposes `statusId` only (no status code/name), so the UI does
 * not render a status column for roles.
 */
export interface RoleResponse {
  id: ID;
  /** Owning application. Null for roles that are not application-scoped. */
  appId?: ID | null;
  code: string;
  name: string;
  /** Free-text on the backend; commonly system / tenant / custom / application. */
  roleType: string;
  isComposite: boolean;
  attributes?: Record<string, unknown> | null;
  description?: string;
  statusId: ID;
  /** Status code from iam.tbl_status (type = ROLE), e.g. ROLE_ACTIVE. */
  statusCode?: string;
  /** Status display name from iam.tbl_status, e.g. Active. */
  statusName?: string;
  createdAt: string;
  createdBy?: ID | null;
  updatedAt?: string | null;
  updatedBy?: ID | null;
  metaData?: Record<string, unknown> | null;
}

/**
 * Payload for POST /Role — matches CreateRoleRequest.cs.
 * `code`, `name` and `roleType` are [Required] on the backend.
 */
export interface CreateRoleRequest {
  appId?: ID | null;
  code: string;
  name: string;
  roleType: string;
  description?: string;
  composite?: boolean;
  compositeRoles?: string[];
  attributes?: Record<string, unknown>;
  /** When true the API also creates the role in Keycloak. */
  createInKeycloak?: boolean;
  realmName?: string;
  /** Required by the API when creating a Keycloak CLIENT role. */
  clientId?: string;
}

/**
 * Payload for PUT /Role/{id} — matches UpdateRoleRequest.cs.
 * Code, role type and application are fixed once created (not updatable).
 */
export interface UpdateRoleRequest {
  name?: string;
  description?: string;
  composite?: boolean;
  compositeRoles?: string[];
  attributes?: Record<string, unknown>;
  /** Status code from iam.tbl_status where type = 'ROLE' (ROLE_ACTIVE / ROLE_INACTIVE). */
  statusCode?: string;
  /**
   * Application the role belongs to. Omit to leave unchanged; send the
   * all-zero GUID to clear it (role becomes non-application-scoped).
   */
  appId?: ID | null;
}

// ============================================================================
// KEYCLOAK SYNC
// ============================================================================

/** A CLIENT role as returned by Keycloak — matches KeycloakRoleResponse.cs. */
export interface KeycloakRoleResponse {
  id: string;
  name: string;
  description?: string;
  composite: boolean;
  clientRole: boolean;
  containerId?: string;
  attributes?: Record<string, unknown> | null;
  compositeRoles?: string[] | null;
}

/** Result of GET /Role/validate/{roleCode} — matches RoleExistenceValidation. */
export interface RoleExistenceValidation {
  roleCode: string;
  realmName: string;
  clientId?: string;
  existsInDatabase: boolean;
  existsInKeycloak: boolean;
  /** Computed server-side: existsInDatabase && existsInKeycloak. */
  inSync: boolean;
  /** Computed server-side: "Realm Role" | "Client Role". */
  roleType: string;
  /** Computed server-side: Synced | Database Only | Keycloak Only | Not Found. */
  status: string;
}

/** Query parameters shared by the Keycloak sync endpoints. */
export interface KeycloakSyncParams {
  realmName: string;
  clientId: string;
  /** Only used by sync-from-keycloak — links imported roles to an application. */
  appId?: ID;
}

// ============================================================================
// SCOPE ASSIGNMENTS
// ============================================================================

export type ScopeType = 'Entity' | 'BusinessUnit' | 'FinancialYear';

/** One assigned scope resource — matches ScopeAssignmentResponseItem. */
export interface ScopeAssignmentItem {
  id: ID;
  code: string;
  name: string;
  isDefault: boolean;
}

/** Defaults summary — matches ScopeDefaultsSummary. */
export interface ScopeDefaultsSummary {
  defaultEntityId?: ID | null;
  defaultEntityName?: string | null;
  defaultBusinessUnitId?: ID | null;
  defaultBusinessUnitName?: string | null;
  defaultFinancialYearId?: ID | null;
  defaultFinancialYearName?: string | null;
}

/** GET /Role/{roleId}/scope-assignments — matches RoleScopeAssignmentResponse. */
export interface RoleScopeAssignmentResponse {
  roleId: ID;
  roleName: string;
  entities: ScopeAssignmentItem[];
  businessUnits: ScopeAssignmentItem[];
  financialYears: ScopeAssignmentItem[];
  defaults: ScopeDefaultsSummary;
}

/** PUT /Role/{roleId}/scope-defaults — matches UpdateRoleScopeDefaultRequest. */
export interface UpdateRoleScopeDefaultRequest {
  scopeType: ScopeType;
  scopeValueId: ID;
}

// ============================================================================
// CONSTANTS
// ============================================================================

/**
 * Role types offered when creating a role. The backend column is free-text, so
 * existing records may carry other values — filter options are derived from the
 * loaded data rather than from this list.
 */
export const ROLE_TYPES = ['system', 'tenant', 'custom', 'application'] as const;
